import type { OrganizationSeason, PartnerSeason, PartnerSeasonMarketingDeliverable as MarketingItem } from '../types';
import type { Squad } from '../../../types';
import { listProgramTerms } from '../../../shared/terms/programTerms';
import { organizationSeasonForTerm } from './organizationSeasons';

export const MARKETING_TYPES: Record<MarketingItem['type'], string> = {
  newsletter: 'Newsletter', outreach: 'Partner outreach', demo: 'Demo', blurb: 'Program blurb',
  email: 'Parent email', flyer: 'Flyer / QR code', social: 'Social post', website: 'Website',
  registration_page: 'Registration page', other: 'Other materials',
};
export const MARKETING_STATUSES: Record<MarketingItem['status'], string> = {
  planned: 'Planned / requested', in_progress: 'In progress / agreed', sent: 'Sent / distributed',
  completed: 'Completed', declined: 'Declined / canceled', not_applicable: 'Not applicable',
};

export function seasonMarketingDeliverables(season?: PartnerSeason | null): MarketingItem[] {
  // An explicit empty list is authoritative; do not resurrect removed legacy items.
  if (Array.isArray(season?.marketing?.deliverables)) return season!.marketing!.deliverables!;
  const legacy = [
    ...(season?.marketing?.plannedDeliverables || []).map((title) => ({ title, owner: 'partner' as const, status: 'planned' as const })),
    ...(season?.marketing?.sentDeliverables || []).map((title) => ({ title, owner: 'partner' as const, status: 'sent' as const })),
    ...(season?.marketing?.independentlyManagedDeliverables || []).map((title) => ({ title, owner: 'overture' as const, status: 'planned' as const })),
  ];
  return legacy.map((item, index) => ({ id: `legacy-${index}`, type: 'other', dueDate: null, ...item }));
}

export function marketingSummary(items: MarketingItem[]) {
  const done = (item: MarketingItem) => ['sent', 'completed'].includes(item.status);
  return {
    newsletters: items.filter((item) => item.type === 'newsletter' && done(item) && item.completedDate).length,
    newsletterOutreach: items.filter((item) => item.type === 'outreach' && item.outreachPurpose === 'newsletter' && done(item) && item.activityDate).length,
    demos: items.filter((item) => item.type === 'demo' && item.status === 'completed' && item.completedDate).length,
    open: items.filter((item) => ['planned', 'in_progress'].includes(item.status)).length,
  };
}

export function marketingSeasonOptions(seasons: PartnerSeason[], squads: Squad[], now = new Date()) {
  const options = new Map(listProgramTerms({ startYear: now.getFullYear() - 1, years: 3 }).map((term) => [term.id, { id: term.id, termId: term.id, label: term.label }]));
  // Resolve canonical season document IDs independently from term labels.
  for (const season of seasons) {
    if (season.id !== season.termId) options.delete(season.termId);
    options.set(season.id, { id: season.id, termId: season.termId || season.id, label: season.label || organizationSeasonForTerm(season.termId)?.label || season.termId || season.id });
  }
  for (const squad of squads) {
    const key = squad.seasonId || squad.termId;
    if (!key || options.has(key) || [...options.values()].some((option) => option.termId === key)) continue;
    options.set(key, { id: key, termId: key, label: organizationSeasonForTerm(key)?.label || key });
  }
  return [...options.values()].sort((a, b) => {
    const aDate = organizationSeasonForTerm(a.termId)?.defaultProgramStartDate || a.termId;
    const bDate = organizationSeasonForTerm(b.termId)?.defaultProgramStartDate || b.termId;
    return bDate.localeCompare(aDate);
  });
}

export function currentMarketingSeason(options: ReturnType<typeof marketingSeasonOptions>, organizationSeasons: OrganizationSeason[], today: string): string {
  const dated = options.map((option) => ({ option, dates: organizationSeasons.find((season) => season.termId === option.termId) || organizationSeasonForTerm(option.termId) }));
  const current = dated.filter(({ dates }) => dates && dates.defaultProgramStartDate <= today && dates.defaultProgramEndDate >= today)
    .sort((a, b) => b.dates!.defaultProgramStartDate.localeCompare(a.dates!.defaultProgramStartDate))[0];
  return current?.option.id || dated.filter(({ dates }) => dates && dates.defaultProgramStartDate > today)
    .sort((a, b) => a.dates!.defaultProgramStartDate.localeCompare(b.dates!.defaultProgramStartDate))[0]?.option.id || options[0]?.id || '';
}

export function squadMatchesMarketingSeason(squad: Squad, id: string, termId: string): boolean {
  const reference = squad.seasonId || squad.termId;
  return Boolean(reference && (reference === id || reference === termId));
}

export function validateMarketingItem(item: MarketingItem): string | null {
  if (!item.title.trim()) return 'Enter a title.';
  if (!(item.type in MARKETING_TYPES) || !(item.status in MARKETING_STATUSES)) return 'Choose a valid type and status.';
  for (const value of [item.dueDate, item.activityDate, item.completedDate, item.sharedDate, item.followUpDate, item.approvedDate]) {
    if (value && (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(Date.parse(value)) || new Date(value).toISOString().slice(0, 10) !== value)) return 'Enter valid dates.';
  }
  if (item.type === 'blurb' && !item.content?.trim()) return 'Enter the program blurb.';
  if (item.type === 'outreach' && !item.outreachPurpose) return 'Choose the outreach purpose.';
  if (item.type === 'outreach' && ['sent', 'completed'].includes(item.status) && !item.activityDate) return 'Enter the date you contacted the partner.';
  if (item.type === 'newsletter' && ['sent', 'completed'].includes(item.status) && !item.completedDate) return 'Enter the actual newsletter distribution date.';
  if (item.type === 'demo' && item.status === 'completed' && !item.completedDate) return 'Enter the date the demo actually ran.';
  for (const value of [item.audienceCount, item.engagementCount]) {
    if (value != null && (!Number.isInteger(value) || value < 0)) return 'Counts must be non-negative whole numbers.';
  }
  for (const value of [item.url, item.registrationUrl]) {
    if (!value) continue;
    try { if (!['https:', 'http:'].includes(new URL(value).protocol)) return 'Links must begin with https:// or http://.'; }
    catch { return 'Enter a complete website link beginning with https:// or http://.'; }
  }
  if ((item.content?.length || 0) + (item.notes?.length || 0) > 100_000) return 'This entry is too long. Link to the full document instead.';
  return null;
}

export const LEGACY_MARKETING_FIELDS: Record<string, string> = {
  reachedOutToDirectorOrMarketing: 'Director / marketing outreach', agreedToDemo: 'Demo agreed', agreedToFlyers: 'Flyers agreed',
  agreedToNewsletter: 'Newsletter agreed', agreedToNewBlurb: 'New blurb agreed', agreedToSocialMediaPost: 'Social post agreed',
  demoRun: 'Demo run', flyersGivenToSchool: 'Flyers given to partner', flyersScannedByParents: 'Flyers scanned by parents',
  newsletter1Sent: 'Newsletter #1 sent', newsletter2Sent: 'Newsletter #2 sent', emailSentToCurrentParents: 'Current-parent email',
  emailSentToPastParents: 'Past-parent email', pastDeliverablesSentToDirectorAndMarketing: 'Past materials shared with director / marketing',
  newBlurbsSentToDirector: 'New blurbs shared with director', socialMediaPostSentToMarketingAndDirector: 'Social post shared with director / marketing',
};
